import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';

export const CASE_PROGRESS_QUERY_KEYS = {
  byUserId: (userId: string) => ['case_progress', 'user', userId] as const,
  byUserAndCase: (userId: string, caseId: string) =>
    ['case_progress', 'user', userId, 'case', caseId] as const,
};

export const useCaseProgress = (userId: string | null) => {
  return useQuery({
    queryKey: CASE_PROGRESS_QUERY_KEYS.byUserId(userId || ''),
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');

      const { data, error } = await supabase
        .from('case_progress')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
};

export const useCaseProgressByCase = (userId: string | null, caseId: string | null) => {
  return useQuery({
    queryKey: CASE_PROGRESS_QUERY_KEYS.byUserAndCase(userId || '', caseId || ''),
    queryFn: async () => {
      if (!userId || !caseId) throw new Error('User ID and Case ID are required');

      const { data, error } = await supabase
        .from('case_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('case_id', caseId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!userId && !!caseId,
  });
};

export const useUpsertCaseProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      caseId,
      updates,
    }: {
      userId: string;
      caseId: string;
      updates: {
        current_slide_id?: string | null;
        visited_slide_ids?: string[];
        completed?: boolean;
        celebration_shown?: boolean;
        score_percentage?: number;
      };
    }) => {
      const { data, error } = await supabase
        .from('case_progress')
        .upsert(
          {
            user_id: userId,
            case_id: caseId,
            ...updates,
          },
          { onConflict: 'user_id,case_id' }
        )
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: CASE_PROGRESS_QUERY_KEYS.byUserId(data.user_id),
      });
      void queryClient.invalidateQueries({
        queryKey: CASE_PROGRESS_QUERY_KEYS.byUserAndCase(data.user_id, data.case_id),
      });
    },
  });
};

export const useResetCaseProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, caseId }: { userId: string; caseId: string }) => {
      const { error } = await supabase
        .from('case_progress')
        .update({
          score_percentage: 0,
          current_slide_id: null,
          completed: false,
          celebration_shown: false,
          visited_slide_ids: [],
        })
        .eq('user_id', userId)
        .eq('case_id', caseId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: CASE_PROGRESS_QUERY_KEYS.byUserId(variables.userId),
      });
      void queryClient.invalidateQueries({
        queryKey: CASE_PROGRESS_QUERY_KEYS.byUserAndCase(variables.userId, variables.caseId),
      });
    },
  });
};
